import React, { useState, useEffect } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import { supabase } from '../lib/supabase'
import AddCategoryInline from './AddCategoryInline'

const teal = '#0D9488'
const plum = '#1E1040'
const plumLight = '#6B21A8'

const ICON_GRID = [
  '🛋️', '🪴', '🎨', '🔨', '📚', '🎵',
  '💊', '⚽', '🎮', '✂️', '💼', '📎',
  '🧰', '🎪', '🚲', '⛽', '💻', '🖨️',
  '📦', '🎁', '🌿', '🍷', '🎂', '⭐',
]

/**
 * ManageCategories
 * Settings section for full category CRUD.
 * Self-contained — loads its own data from Supabase.
 */
export default function ManageCategories() {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(true)
  const [categories, setCategories] = useState([])
  const [tab, setTab] = useState('business') // 'business' | 'personal'
  const [adding, setAdding] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [editName, setEditName] = useState('')
  const [editIcon, setEditIcon] = useState('')
  const [editError, setEditError] = useState('')
  const [deleteConfirmId, setDeleteConfirmId] = useState(null)
  const [deleteError, setDeleteError] = useState('')

  useEffect(() => {
    loadCategories()
  }, [])

  const loadCategories = async () => {
    setLoading(true)
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) { setLoading(false); return }

    const { data } = await supabase
      .from('categories')
      .select('*')
      .eq('user_id', user.id)
      .order('sort_order')

    setCategories(data || [])
    setLoading(false)
  }

  const handleAdded = (newCat) => {
    setCategories(prev => [...prev, newCat])
    setAdding(false)
  }

  const startEdit = (cat) => {
    setEditingId(cat.id)
    setEditName(cat.name)
    setEditIcon(cat.icon || '📦')
    setEditError('')
    // Cancel any other UI state
    setAdding(false)
    setDeleteConfirmId(null)
  }

  const cancelEdit = () => {
    setEditingId(null)
    setEditName('')
    setEditIcon('')
    setEditError('')
  }

  const saveEdit = async () => {
    const trimmed = editName.trim()
    if (!trimmed) {
      setEditError(t('manage_categories.error_name_required'))
      return
    }
    if (trimmed.length > 40) {
      setEditError(t('manage_categories.error_too_long'))
      return
    }

    const cat = categories.find(c => c.id === editingId)
    if (!cat) return

    // Check duplicate within the same category_type (excluding this cat)
    const duplicate = categories.find(c =>
      c.id !== editingId
      && c.category_type === cat.category_type
      && c.name.toLowerCase() === trimmed.toLowerCase()
    )
    if (duplicate) {
      setEditError(t('manage_categories.error_duplicate', { name: trimmed }))
      return
    }

    const { data: updated, error: err } = await supabase
      .from('categories')
      .update({ name: trimmed, icon: editIcon })
      .eq('id', editingId)
      .select()
      .single()

    if (err) {
      setEditError(err.message || t('manage_categories.error_save'))
      return
    }

    setCategories(prev => prev.map(c => c.id === updated.id ? updated : c))
    cancelEdit()
  }

  const startDelete = (cat) => {
    setDeleteConfirmId(cat.id)
    setDeleteError('')
    setEditingId(null)
  }

  const cancelDelete = () => {
    setDeleteConfirmId(null)
    setDeleteError('')
  }

  const confirmDelete = async () => {
    const cat = categories.find(c => c.id === deleteConfirmId)
    if (!cat) return

    // Check if any receipts use this category
    const { count, error: countErr } = await supabase
      .from('transactions')
      .select('id', { count: 'exact', head: true })
      .eq('category_id', cat.id)

    if (countErr) {
      setDeleteError(countErr.message || t('manage_categories.error_check_receipts'))
      return
    }

    if (count && count > 0) {
      setDeleteError(t('manage_categories.error_in_use', { count }))
      return
    }

    const { error: err } = await supabase
      .from('categories')
      .delete()
      .eq('id', cat.id)

    if (err) {
      setDeleteError(err.message || t('manage_categories.error_delete'))
      return
    }

    setCategories(prev => prev.filter(c => c.id !== cat.id))
    setDeleteConfirmId(null)
    setDeleteError('')
  }

  const visibleCategories = categories.filter(c =>
    tab === 'business' ? c.category_type !== 'personal' : c.category_type === 'personal'
  )

  const tabAccent = tab === 'personal' ? plumLight : teal

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>{t('manage_categories.title')}</h2>
          <p style={styles.subtitle}>
            {t('manage_categories.subtitle')}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div style={styles.pillRow}>
        <div style={styles.pillGroup}>
          <button
            style={{ ...styles.pill, ...(tab === 'business' ? styles.pillActive : {}) }}
            onClick={() => { setTab('business'); cancelEdit(); cancelDelete(); setAdding(false) }}
          >
            💼 {t('manage_categories.tab_business')} ({categories.filter(c => c.category_type !== 'personal').length})
          </button>
          <button
            style={{ ...styles.pill, ...(tab === 'personal' ? styles.pillActive : {}) }}
            onClick={() => { setTab('personal'); cancelEdit(); cancelDelete(); setAdding(false) }}
          >
            🏠 {t('manage_categories.tab_personal')} ({categories.filter(c => c.category_type === 'personal').length})
          </button>
        </div>
        <button
          style={{ ...styles.addBtn, background: tabAccent }}
          onClick={() => { setAdding(true); cancelEdit(); cancelDelete() }}
        >
          + {t('manage_categories.add_btn')}
        </button>
      </div>

      {/* Inline add form */}
      {adding && (
        <AddCategoryInline
          categoryType={tab}
          variant={tab}
          onAdded={handleAdded}
          onCancel={() => setAdding(false)}
        />
      )}

      {/* List */}
      {loading ? (
        <div style={styles.loading}>{t('common.loading')}</div>
      ) : visibleCategories.length === 0 ? (
        <div style={styles.empty}>
          {tab === 'personal'
            ? t('manage_categories.empty_personal')
            : t('manage_categories.empty_business')}
        </div>
      ) : (
        <div style={styles.list}>
          {visibleCategories.map(cat => (
            <div key={cat.id} style={styles.row}>
              {editingId === cat.id ? (
                // === Edit mode ===
                <div style={styles.editBlock}>
                  <div style={styles.editLabel}>{t('manage_categories.edit_label')}</div>
                  <div style={styles.iconGrid}>
                    {ICON_GRID.map(emoji => (
                      <button
                        key={emoji}
                        type="button"
                        style={{
                          ...styles.iconBtn,
                          ...(editIcon === emoji ? { ...styles.iconBtnActive, borderColor: tabAccent } : {}),
                        }}
                        onClick={() => setEditIcon(emoji)}
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                  <input
                    type="text"
                    style={styles.input}
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.preventDefault(); saveEdit() }
                      if (e.key === 'Escape') { e.preventDefault(); cancelEdit() }
                    }}
                    maxLength={40}
                    autoFocus
                  />
                  {editError && <div style={styles.error}>{editError}</div>}
                  <div style={styles.editActions}>
                    <button style={styles.btnSecondary} onClick={cancelEdit}>{t('common.cancel')}</button>
                    <button style={{ ...styles.btnPrimary, background: tabAccent }} onClick={saveEdit}>
                      {t('manage_categories.save_changes')}
                    </button>
                  </div>
                </div>
              ) : deleteConfirmId === cat.id ? (
                // === Delete confirm ===
                <div style={styles.deleteBlock}>
                  <div style={styles.deleteText}>
                    <Trans
                      i18nKey="manage_categories.delete_confirm"
                      values={{ icon: cat.icon, name: cat.name }}
                      components={{ strong: <strong /> }}
                    />
                    {cat.is_default && ' ' + t('manage_categories.delete_is_default_note')}
                  </div>
                  {deleteError && <div style={styles.error}>{deleteError}</div>}
                  <div style={styles.editActions}>
                    <button style={styles.btnSecondary} onClick={cancelDelete}>{t('common.cancel')}</button>
                    <button style={styles.btnDanger} onClick={confirmDelete}>
                      {t('manage_categories.confirm_delete')}
                    </button>
                  </div>
                </div>
              ) : (
                // === Normal view ===
                <>
                  <div style={styles.rowMain}>
                    <span style={styles.rowIcon}>{cat.icon}</span>
                    <span style={styles.rowName}>{cat.name}</span>
                    {cat.is_default && <span style={styles.defaultBadge}>{t('manage_categories.default_badge')}</span>}
                  </div>
                  <div style={styles.rowActions}>
                    <button style={styles.actionBtn} onClick={() => startEdit(cat)}>
                      {t('common.edit')}
                    </button>
                    <button style={styles.actionBtnDanger} onClick={() => startDelete(cat)}>
                      {t('common.delete')}
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

const styles = {
  container: {
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    color: plum,
  },
  header: { marginBottom: '18px' },
  title: {
    fontFamily: "'Fraunces', serif",
    fontSize: '22px',
    fontWeight: 800,
    color: plum,
    letterSpacing: '-0.3px',
    margin: 0,
    marginBottom: '4px',
  },
  subtitle: {
    fontSize: '13px',
    color: '#6B7280',
    fontWeight: 500,
    margin: 0,
    lineHeight: 1.5,
  },
  pillRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
    gap: '12px',
    flexWrap: 'wrap',
  },
  pillGroup: {
    display: 'inline-flex',
    background: '#F3F4F6',
    padding: '4px',
    borderRadius: '100px',
    gap: '4px',
  },
  pill: {
    padding: '8px 16px',
    background: 'transparent',
    border: 'none',
    borderRadius: '100px',
    fontSize: '13px',
    fontWeight: 700,
    color: '#6B7280',
    cursor: 'pointer',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    transition: 'all 0.15s',
  },
  pillActive: {
    background: '#fff',
    color: plum,
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  addBtn: {
    padding: '10px 18px',
    color: '#fff',
    border: 'none',
    borderRadius: '100px',
    fontSize: '13px',
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
  },
  loading: { padding: '32px', textAlign: 'center', color: '#9CA3AF', fontSize: '14px' },
  empty: {
    padding: '32px',
    textAlign: 'center',
    color: '#9CA3AF',
    fontSize: '14px',
    background: '#F9FAFB',
    border: '1px dashed #D1D5DB',
    borderRadius: '12px',
  },
  list: {
    background: '#fff',
    border: '1px solid #E5E7EB',
    borderRadius: '14px',
    overflow: 'hidden',
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '14px 18px',
    borderTop: '1px solid #F3F4F6',
    gap: '12px',
    flexWrap: 'wrap',
  },
  rowMain: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flex: 1,
    minWidth: 0,
  },
  rowIcon: { fontSize: '22px' },
  rowName: { fontSize: '14px', fontWeight: 600, color: plum },
  defaultBadge: {
    fontSize: '10px',
    fontWeight: 700,
    color: '#6B7280',
    background: '#F3F4F6',
    padding: '3px 8px',
    borderRadius: '4px',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  rowActions: {
    display: 'flex',
    gap: '8px',
  },
  actionBtn: {
    padding: '6px 14px',
    background: '#fff',
    color: plum,
    border: '1.5px solid #E5E7EB',
    borderRadius: '100px',
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
  },
  actionBtnDanger: {
    padding: '6px 14px',
    background: '#fff',
    color: '#DC2626',
    border: '1.5px solid #FECACA',
    borderRadius: '100px',
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
  },
  editBlock: {
    flex: 1,
    background: '#F9FAFB',
    padding: '12px',
    borderRadius: '10px',
  },
  editLabel: {
    fontSize: '11px',
    fontWeight: 700,
    color: '#6B7280',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: '10px',
  },
  iconGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(6, 1fr)',
    gap: '6px',
    marginBottom: '12px',
  },
  iconBtn: {
    aspectRatio: '1/1',
    background: '#fff',
    border: '1.5px solid #E5E7EB',
    borderRadius: '8px',
    fontSize: '20px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
  },
  iconBtnActive: {
    background: '#fff',
    borderWidth: '2.5px',
    transform: 'scale(1.05)',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: '8px',
    border: '1.5px solid #E5E7EB',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
    color: plum,
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    background: '#fff',
  },
  error: {
    background: '#FEE2E2',
    color: '#991B1B',
    padding: '8px 12px',
    borderRadius: '8px',
    fontSize: '12px',
    fontWeight: 500,
    marginTop: '10px',
  },
  editActions: {
    display: 'flex',
    gap: '8px',
    marginTop: '12px',
    justifyContent: 'flex-end',
  },
  btnPrimary: {
    padding: '9px 18px',
    color: '#fff',
    border: 'none',
    borderRadius: '100px',
    fontSize: '12px',
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
  },
  btnSecondary: {
    padding: '9px 18px',
    background: '#fff',
    color: plum,
    border: '1.5px solid #E5E7EB',
    borderRadius: '100px',
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
  },
  btnDanger: {
    padding: '9px 18px',
    background: '#DC2626',
    color: '#fff',
    border: 'none',
    borderRadius: '100px',
    fontSize: '12px',
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
  },
  deleteBlock: {
    flex: 1,
    background: '#FEF3C7',
    padding: '12px',
    borderRadius: '10px',
  },
  deleteText: {
    fontSize: '13px',
    color: '#92400E',
    fontWeight: 500,
    lineHeight: 1.5,
  },
}
